/**
 * @genesis_domain Auth Engine - Registration Form
 * @genesis_version V9.1
 * @genesis_intent Bento UI registration form with semantic glass design tokens. Binds to
 *   useRegistrationViewModel via native React 19 form action. Delegates OTP verification
 *   to OTPVerification sub-component.
 * @genesis_boundaries No business logic, no direct API calls. Pure presentational component.
 * @genesis_ai_trail V1.0: Created during Feature Sprint 1 — Multi-Tenant Onboarding.
 * @genesis_ai_trail V1.1: Added isVerificationPending state.
 * @genesis_ai_trail V1.2: Extracted OTP UI to OTPVerification.tsx.
 * @genesis_ai_trail V1.3: Global Semantic Strike — eradicated all literal Tailwind colors
 *   (bg-white/*, border-white/*), all dark: modifiers, and hardcoded blurs.
 */

import { OTPVerification } from '@/domains/auth/components/OTPVerification';
import { useRegistrationViewModel } from '@/domains/auth/hooks/useRegistrationViewModel';
import { UserPlus } from 'lucide-react';
import { useFormStatus } from 'react-dom';

function SubmitButton() {
    const { pending } = useFormStatus();
    return (
        <button
            type="submit"
            disabled={pending}
            className="w-full px-6 py-3 text-[var(--color-content-inverse)] font-semibold rounded-[16px]
                       bg-[var(--color-primary-base)] hover:bg-[var(--color-primary-hover)]
                       disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
        >
            {pending ? (
                <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating Account...
                </span>
            ) : (
                <span className="flex items-center justify-center gap-2">
                    <UserPlus className="w-5 h-5" />
                    Create Account
                </span>
            )}
        </button>
    );
}

export function RegistrationForm() {
    const { state, formAction, isPending } = useRegistrationViewModel();

    if (state.isVerificationPending) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
                <div className="w-full max-w-md">
                    <div className="theme-card backdrop-blur-3xl rounded-[24px]
                                    border border-[var(--color-glass-border)]
                                    shadow-[var(--shadow-card)] p-8 sm:p-10">
                        <OTPVerification
                            email={state.email}
                            formAction={formAction}
                            isPending={isPending}
                            error={state.error}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8">
            <div className="w-full max-w-md">
                <div className="theme-card backdrop-blur-3xl rounded-[24px]
                                border border-[var(--color-glass-border)]
                                shadow-[var(--shadow-card)] p-8 sm:p-10">

                    <div className="flex items-center gap-3 mb-8">
                        <UserPlus className="w-6 h-6 text-[var(--color-primary-base)]" />
                        <h1 className="text-2xl font-bold text-[var(--color-content-primary)]">
                            Create Account
                        </h1>
                    </div>

                    {state.error && (
                        <div className="mb-6 px-4 py-3 rounded-[16px]
                                        bg-[var(--color-danger-subtle)] border border-[var(--color-danger-subtle)]
                                        text-[var(--color-danger-base)] text-sm">
                            {state.error}
                        </div>
                    )}

                    {state.success && (
                        <div className="mb-6 px-4 py-3 rounded-[16px]
                                        bg-[var(--color-success-subtle)] border border-[var(--color-success-subtle)]
                                        text-[var(--color-success-base)] text-sm">
                            Account created successfully! Redirecting...
                        </div>
                    )}

                    <form action={formAction} className="space-y-5">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-[var(--color-content-secondary)] mb-1.5">
                                Full Name
                            </label>
                            <input
                                id="name" name="name" type="text" required disabled={isPending}
                                placeholder="Enter your full name"
                                className="w-full px-4 py-3 rounded-[16px] theme-input shadow-inner
                                           border border-[var(--color-input-border)]
                                           text-[var(--color-input-text)] placeholder-[var(--color-content-tertiary)]
                                           focus:outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)]
                                           focus:border-[var(--color-primary-base)]
                                           disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                            />
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-[var(--color-content-secondary)] mb-1.5">
                                Email Address
                            </label>
                            <input
                                id="email" name="email" type="email" required disabled={isPending}
                                placeholder="you@example.com"
                                className="w-full px-4 py-3 rounded-[16px] theme-input shadow-inner
                                           border border-[var(--color-input-border)]
                                           text-[var(--color-input-text)] placeholder-[var(--color-content-tertiary)]
                                           focus:outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)]
                                           focus:border-[var(--color-primary-base)]
                                           disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-[var(--color-content-secondary)] mb-1.5">
                                Password
                            </label>
                            <input
                                id="password" name="password" type="password" required minLength={6}
                                disabled={isPending} placeholder="At least 6 characters"
                                className="w-full px-4 py-3 rounded-[16px] theme-input shadow-inner
                                           border border-[var(--color-input-border)]
                                           text-[var(--color-input-text)] placeholder-[var(--color-content-tertiary)]
                                           focus:outline-none focus:ring-2 focus:ring-[var(--color-focus-ring)]
                                           focus:border-[var(--color-primary-base)]
                                           disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                            />
                        </div>

                        <SubmitButton />
                    </form>

                    <p className="mt-6 text-center text-sm text-[var(--color-content-tertiary)]">
                        Already have an account?{' '}
                        <a href="/login" className="text-[var(--color-primary-base)] hover:underline font-medium">
                            Sign in
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}